// Human Task GATES in the Task-detail modal. The panel routes decisions through the same dashboard API
// seams as the Approvals inbox, then re-reads the Task projection on success. It never authors lifecycle
// state locally: creator/owner affordances, readiness, revision, and waiting party are all server-derived.

import { useState } from 'react';
import { useSurfaceApi } from '../surfaces/SurfaceApiContext.tsx';
import type { RouteResult } from '../surfaces/api-client.ts';
import type { TaskDetailProjection } from '../surfaces/know-projections.ts';
import type { TaskReviewAction } from '../surfaces/task-review-api-types.ts';
import { notifyTaskProjectionsChanged } from '../surfaces/task-projection-events.ts';
import { Btn } from '../surfaces/primitives.tsx';
import { useTaskCancellation, type TaskCancellationDecision } from './useTaskCancellation.ts';
import { TaskOwnerControl } from './TaskOwnerControl.tsx';

type ReviewFormMode = 'request_draft_changes' | 'request_changes' | 'defer' | 'cancel';
type ReviewForm = ReviewFormMode | null;

const WAITING_COPY = {
  draft_reviewer: 'Waiting for the draft reviewer to approve this definition.',
  owner: 'Waiting for the task owner to approve and start this brief.',
} as const;

interface TaskReviewPanelProps {
  readonly detail: TaskDetailProjection;
  readonly reload: () => void;
  readonly onActionNotice: (notice: string) => void;
}

/** Route one decision and reconcile to the Task-owned snapshot only after the server accepts it. */
function useTaskDecision(
  detail: TaskDetailProjection,
  reload: () => void,
): {
  readonly busy: boolean;
  readonly note: string | null;
  readonly approve: (kind: 'task_draft' | 'task_start') => Promise<void>;
  readonly review: (action: TaskReviewAction, reason: string, revisitAt?: string) => Promise<void>;
} {
  const api = useSurfaceApi();
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const settle = async (route: () => Promise<RouteResult>): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setNote(null);
    try {
      const result = await route();
      if (result.ok) {
        notifyTaskProjectionsChanged();
        reload();
      } else setNote(result.note ?? 'That decision was not permitted.');
    } catch {
      setNote('That decision could not reach the task. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return {
    busy,
    note,
    approve: (kind) => settle(() => api.exerciseGate(kind, detail.id, detail.definitionRevision)),
    review: (action, reason, revisitAt) =>
      settle(() =>
        api.reviewTask(
          detail.id,
          action,
          revisitAt === undefined
            ? { reason, expectedRevision: detail.definitionRevision }
            : { reason, expectedRevision: detail.definitionRevision, revisitAt },
        ),
      ),
  };
}

function RevisitDateInput({
  value,
  onChange,
}: {
  readonly value: string;
  readonly onChange: (value: string) => void;
}): React.JSX.Element {
  return (
    <label>
      Revisit at (local time)
      <input
        type="datetime-local"
        value={value}
        required
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

function ReviewReasonInput({
  mode,
  value,
  onChange,
}: {
  readonly mode: Exclude<ReviewForm, null>;
  readonly value: string;
  readonly onChange: (value: string) => void;
}): React.JSX.Element {
  return (
    <label>
      {mode === 'defer'
        ? 'Reason for deferring task'
        : mode === 'cancel'
          ? 'Reason for cancelling task'
          : 'Changes requested'}
      <textarea
        rows={3}
        value={value}
        required
        placeholder={
          mode === 'defer'
            ? 'Why should this wait?'
            : mode === 'cancel'
              ? 'Why should this task stop?'
              : 'What should be changed before you approve it?'
        }
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

/** Mandatory-reason owner response. A deferral also requires the explicit revisit date. */
function ReviewFormBody({
  mode,
  busy,
  onCancel,
  onSubmit,
}: {
  readonly mode: Exclude<ReviewForm, null>;
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onSubmit: (reason: string, revisitAt?: string) => void;
}): React.JSX.Element {
  const [reason, setReason] = useState('');
  const [revisitAt, setRevisitAt] = useState('');
  const valid =
    reason.trim().length > 0 &&
    (mode !== 'defer' || /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(revisitAt));
  return (
    <form
      className="task-review-form"
      aria-label={
        mode === 'defer' ? 'Defer task' : mode === 'cancel' ? 'Cancel task' : 'Request task changes'
      }
      onSubmit={(event) => {
        event.preventDefault();
        if (!valid || busy) return;
        onSubmit(reason.trim(), mode === 'defer' ? new Date(revisitAt).toISOString() : undefined);
      }}
    >
      {mode === 'defer' ? <RevisitDateInput value={revisitAt} onChange={setRevisitAt} /> : null}
      <ReviewReasonInput mode={mode} value={reason} onChange={setReason} />
      <div className="task-review-form-actions">
        <Btn type="submit" sm disabled={!valid || busy}>
          {mode === 'defer'
            ? 'Confirm deferral'
            : mode === 'cancel'
              ? 'Confirm cancellation'
              : 'Send changes'}
        </Btn>
        <Btn kind="ghost" sm disabled={busy} onClick={onCancel}>
          {mode === 'cancel' ? 'Keep task' : 'Cancel'}
        </Btn>
      </div>
    </form>
  );
}

type TaskDecision = ReturnType<typeof useTaskDecision>;

function DraftGate({
  detail,
  decision,
  onForm,
}: {
  readonly detail: TaskDetailProjection;
  readonly decision: TaskDecision;
  readonly onForm: (form: ReviewFormMode) => void;
}): React.JSX.Element | null {
  if (!detail.canApproveDraft) return null;
  const blocked = !detail.draftReadiness.ready;
  return (
    <>
      <p>You are reviewing definition revision {detail.definitionRevision}.</p>
      <div className="task-review-actions">
        <Btn
          disabled={decision.busy || blocked}
          ariaLabel={`Approve draft: ${detail.title}`}
          onClick={() => void decision.approve('task_draft')}
        >
          Approve draft
        </Btn>
        <Btn
          kind="ghost"
          sm
          disabled={decision.busy}
          onClick={() => onForm('request_draft_changes')}
        >
          Request changes
        </Btn>
      </div>
      {blocked ? <p className="task-review-warning">{detail.draftReadiness.note}</p> : null}
    </>
  );
}

function StartGate({
  detail,
  decision,
  onForm,
}: {
  readonly detail: TaskDetailProjection;
  readonly decision: TaskDecision;
  readonly onForm: (form: ReviewFormMode) => void;
}): React.JSX.Element | null {
  if (!detail.canApproveStart) return null;
  return (
    <>
      <p>Approve this brief to release Ritona to begin the work.</p>
      <div className="task-review-actions">
        <Btn
          disabled={decision.busy}
          ariaLabel={`Approve and start: ${detail.title}`}
          onClick={() => void decision.approve('task_start')}
        >
          Approve &amp; start
        </Btn>
        <Btn kind="ghost" sm disabled={decision.busy} onClick={() => onForm('request_changes')}>
          Request changes
        </Btn>
        <Btn kind="ghost" sm disabled={decision.busy} onClick={() => onForm('defer')}>
          Defer
        </Btn>
      </div>
    </>
  );
}

function CancellationControl({
  detail,
  cancellation,
  onForm,
}: {
  readonly detail: TaskDetailProjection;
  readonly cancellation: TaskCancellationDecision;
  readonly onForm: (form: ReviewFormMode) => void;
}): React.JSX.Element | null {
  if (!detail.canCancel) return null;
  return (
    <>
      <p>Stop this task and its active or queued Ritona work.</p>
      <Btn kind="ghost" sm disabled={cancellation.busy} onClick={() => onForm('cancel')}>
        Cancel task
      </Btn>
    </>
  );
}

function DecisionNotes({
  decision,
  cancellation,
}: {
  readonly decision: TaskDecision;
  readonly cancellation: TaskCancellationDecision;
}): React.JSX.Element {
  const notes = [decision.note, cancellation.note].filter((note) => note !== null);
  return (
    <>
      {notes.map((note) => (
        <p key={note} role="status" className="gate-note task-review-note">
          {note}
        </p>
      ))}
    </>
  );
}

/** Creator and owner Gate controls, rendered only from server-owned affordances. */
export function TaskReviewPanel({
  detail,
  reload,
  onActionNotice,
}: TaskReviewPanelProps): React.JSX.Element {
  const [form, setForm] = useState<ReviewForm>(null);
  const decision = useTaskDecision(detail, reload);
  const cancellation = useTaskCancellation(detail, reload, onActionNotice);
  const waitingCopy = detail.waitingOn === null ? null : WAITING_COPY[detail.waitingOn];
  const incompleteDraft =
    detail.state === 'draft' && !detail.canApproveDraft && !detail.draftReadiness.ready;

  return (
    <section
      className="task-review"
      aria-label={detail.canCancel ? 'Task control' : 'Task approval'}
    >
      <div className="m-rk">{detail.canCancel ? 'Task control' : 'Approval'}</div>
      <div className="task-review-card">
        <DraftGate detail={detail} decision={decision} onForm={setForm} />
        <TaskOwnerControl detail={detail} reload={reload} />
        <StartGate detail={detail} decision={decision} onForm={setForm} />
        <CancellationControl detail={detail} cancellation={cancellation} onForm={setForm} />

        {!detail.canApproveDraft && !detail.canApproveStart && waitingCopy !== null ? (
          <p className="task-review-waiting">{waitingCopy}</p>
        ) : null}
        {incompleteDraft ? (
          <p className="task-review-warning">{detail.draftReadiness.note}</p>
        ) : null}

        {form !== null ? (
          <ReviewFormBody
            key={form}
            mode={form}
            busy={decision.busy || cancellation.busy}
            onCancel={() => setForm(null)}
            onSubmit={(reason, revisitAt) => {
              if (form === 'cancel') void cancellation.cancel(reason);
              else void decision.review(form, reason, revisitAt);
            }}
          />
        ) : null}

        <DecisionNotes decision={decision} cancellation={cancellation} />
      </div>
    </section>
  );
}
